// Dynamic API Configuration
export interface ApiConfig {
  baseUrl: string;
  endpoints: {
    contact: string;
    // Add more endpoints as needed
  };
  timeout: number;
  retryAttempts: number;
}

// Environment detection
export const getEnvironment = (): 'development' | 'staging' | 'production' => {
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  } else if (hostname.includes('staging') || hostname.includes('test')) {
    return 'staging';
  } else {
    return 'production';
  }
};

// Dynamic API configurations
export const apiConfigs: Record<string, ApiConfig> = {
  development: {
    baseUrl: 'http://localhost/intenseinfotech/backend/api',
    endpoints: {
      contact: '/contact/submit.php',
    },
    timeout: 10000,
    retryAttempts: 3,
  },
  staging: {
    baseUrl: 'https://staging-backend.intenseinfotech.com/api',
    endpoints: {
      contact: '/contact/submit.php',
    },
    timeout: 15000,
    retryAttempts: 2,
  },
  production: {
    baseUrl: 'https://backend.intenseinfotech.com/api',
    endpoints: {
      contact: '/contact/submit.php',
    },
    timeout: 20000,
    retryAttempts: 3,
  },
};

// Get current API configuration
export const getApiConfig = (): ApiConfig => {
  const env = getEnvironment();
  const customUrl = process.env.REACT_APP_API_BASE_URL;
  
  if (customUrl) {
    return {
      baseUrl: customUrl,
      endpoints: apiConfigs[env].endpoints,
      timeout: apiConfigs[env].timeout,
      retryAttempts: apiConfigs[env].retryAttempts,
    };
  }
  
  // In development, if localhost is not accessible, fallback to production
  if (env === 'development') {
    // Check if we're in browser (window exists)
    if (typeof window !== 'undefined') {
      // Try production API if localhost fails
      const useProduction = localStorage.getItem('useProductionAPI') === 'true';
      if (useProduction) {
        console.warn('⚠️ Using production API due to localhost connection issues');
        return apiConfigs.production;
      }
    }
  }
  
  return apiConfigs[env];
};

// Build full API URL
export const buildApiUrl = (endpoint: keyof ApiConfig['endpoints']): string => {
  const config = getApiConfig();
  return `${config.baseUrl}${config.endpoints[endpoint]}`;
};

// API Response interface
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  error?: string;
  data?: T;
  submission_id?: number;
}
